import {
  Button,
  Header,
  ScrollContainer,
  StickyFooterButtons,
  Typography,
  useBreakpoints,
  useLoading,
} from "best-common-react";
import React, { useEffect, useMemo, useState } from "react";
import { getEvent, getVenueAcceptedBillings, saveRequest } from "../../api/RequesTixApi";
import RequestInformation from "../../components/requests/form/RequestInformation";
import TicketRequestForm from "../../components/requests/form/TicketRequestForm";
import TicketAgreement from "../../components/tickets/TicketAgreement";
import RouteConstants from "../../constants/RouteConstants";
import { TicketRequestAllotmentTypes } from "../../constants/TicketRequestConstants";
import { useAuth } from "../../contexts/AuthContext";
import { TicketRequestProvider } from "../../contexts/TicketRequestContext";
import { UserAllotmentCountDTO } from "../../types/Allotment";
import { GameDTO } from "../../types/Game";
import { SeasonTypeCode } from "../../types/Season";
import { FlattenedTicketRequest, SaveTicketRequestDTO } from "../../types/TicketRequest";
import { useAllotmentAmounts } from "../../util/AllotmentUtil";
import { getSeasonTypeForGame } from "../../util/SeasonUtil";
import { capitalizeFirstLetter } from "../../util/StringUtil";
import { isBusiness, isCompOnly, useRequestValidation, useValidCreditCard } from "../../util/TicketRequestUtil";
import { useLocation, useNavigate, useParams } from "react-router";
import { RequestTypeDesc } from "../../types/RequestType";
import { VenueAcceptedBilling } from "../../types/Venue";
import clsx from "clsx";

type TicketRequestCompParams = {
  type: RequestTypeDesc;
  gamePk: string;
};

const TicketRequestComp = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setLoading } = useLoading();
  const { userInfo, isAdmin } = useAuth();
  const { isTabletOrMobile } = useBreakpoints();
  const { gamePk: gamePkParam, type } = useParams<TicketRequestCompParams>();
  const gamePk = parseInt(gamePkParam!);
  const [ticketRequest, setTicketRequest] = useState<FlattenedTicketRequest>({
    // @ts-expect-error
    type: type,
    //@ts-expect-error
    submitter: userInfo,
    //@ts-expect-error
    requester: userInfo,
    notifyRequester: false,
    // @ts-expect-error
    originalGamePk: null,
    gamePk: gamePk,
    projectId: -1,
    businessId: null,
    // @ts-expect-error
    requestType: undefined,
    // @ts-expect-error
    quantity: undefined,
    approvingSupervisor: {
      // @ts-expect-error
      employeeUserId: null,
    },
    maxPriceOptionId: null,
    userBilling: null,
    recipient: "",
    recipientFirst: "",
    recipientLast: "",
    deliveryEmail: "",
    forGovernmentUse: false,
    forCharityUse: false,
    comments: "",
    surroundingGames: [],
    phoneNumber: "",
  });

  const [accessible, setAccessible] = useState<boolean>(false);
  const [canSave, setCanSave] = useState<boolean>(false);
  const [allotment, setAllotment] = useState<UserAllotmentCountDTO | undefined>(undefined);
  const [allotments, setAllotments] = useState<UserAllotmentCountDTO[]>([]);
  const [game, setGame] = useState<GameDTO | undefined>(undefined);
  const [hasBusinessAllotment, setHasBusinessAllotment] = useState<boolean>(false);
  const [hasPersonalAllotment, setHasPersonalAllotment] = useState<boolean>(false);
  const [venueBillings, setVenueBillings] = useState<VenueAcceptedBilling[]>([]);
  const [agreed, setAgreed] = useState<boolean>(false);

  const isFormValid = useRequestValidation(ticketRequest, allotment, game?.allowSmsTickets);
  const isCardValid = useValidCreditCard(ticketRequest, venueBillings, game?.venue);
  const isBusinessType = useMemo(() => isBusiness(type), [type]);

  const save = async () => {
    try {
      setLoading(true);
      const { submitter, requester, approvingSupervisor, ...rest } = ticketRequest;
      const toSave: SaveTicketRequestDTO = {
        // @ts-expect-error
        submitterId: submitter.employeeUserId,
        // @ts-expect-error
        requesterId: requester.employeeUserId,
        approvingSupervisorId: approvingSupervisor.employeeUserId,
        ...rest,
      };
      await saveRequest(toSave);
      cancel();
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const cancel = () => {
    const url = !!location?.state?.previousLocation ? location.state.previousLocation : RouteConstants.BASE;
    navigate(url);
  };

  useEffect(() => {
    if (!!game) {
      const season: SeasonTypeCode = getSeasonTypeForGame(game);
      const allot: UserAllotmentCountDTO | undefined = allotments.find((allotment: UserAllotmentCountDTO) => {
        return (
          allotment.season?.seasonType?.seasonTypeId === season.id &&
          allotment.season.year.toString() === game.season.toString() &&
          (isBusiness(type)
            ? allotment.allotmentType.allotmentTypeId === TicketRequestAllotmentTypes.BUSINESS
            : allotment.allotmentType.allotmentTypeId === TicketRequestAllotmentTypes.PERSONAL)
        );
      });
      setAllotment(allot);
    }
  }, [allotments, game, type]);

  useEffect(() => {
    getEvent(gamePk).then((data: GameDTO) => {
      setGame(data);
    });
  }, [gamePk]);

  useEffect(() => {
    if (game?.venue?.id) {
      getVenueAcceptedBillings(game.venue.id).then((data) => {
        setVenueBillings(data);
      });
    }
  }, [game]);

  useEffect(() => {
    if (!!game) {
      const { hasBusinessAllotment, hasPersonalAllotment } = useAllotmentAmounts(
        allotments,
        game.season,
        game.gameType,
      );
      setHasBusinessAllotment(hasBusinessAllotment);
      setHasPersonalAllotment(hasPersonalAllotment);
    }
  }, [game, allotments]);

  useEffect(() => {
    const hasAllotmentLeft = !isCompOnly(ticketRequest) || isBusinessType ? hasBusinessAllotment : hasPersonalAllotment;
    const hasAgreed = isBusinessType ? true : agreed;
    setCanSave(isFormValid && hasAgreed && isCardValid && ((accessible && hasAllotmentLeft) || isAdmin));
  }, [ticketRequest, isFormValid, allotments, isCardValid, agreed, isBusiness]);

  return (
    <TicketRequestProvider gamePk={gamePkParam}>
      <Header
        sticky
        title={
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <div>{`${capitalizeFirstLetter(type)} Request`}</div>
              <div>
                <Typography variant="bodySmall">
                  {ticketRequest.requester?.lastName}, {ticketRequest.requester?.firstName}
                </Typography>
              </div>
            </div>
            <div className="d-flex bcr-gap-4 align-items-center">
              <div className="d-flex flex-column align-items-end">
                {!!ticketRequest.quantity && (
                  <Typography variant="bodyLarge">{ticketRequest.quantity} Ticket(s)</Typography>
                )}
                <Typography variant="bodySmall">{ticketRequest.requestType?.requestTypeName}</Typography>
              </div>
              <StickyFooterButtons startsOpen={true}>
                <Button variant="primary" disabled={!canSave} onClick={save}>
                  Submit
                </Button>
                <Button variant="default" onClick={cancel}>
                  Cancel
                </Button>
              </StickyFooterButtons>
            </div>
          </div>
        }
      />
      <ScrollContainer>
        <div className={clsx("bcr-p-2 d-flex", { "flex-wrap": isTabletOrMobile })} style={{ overflowY: "visible" }}>
          <RequestInformation
            originalGamePk={ticketRequest.originalGamePk}
            setAccessible={setAccessible}
            userId={ticketRequest.requester.employeeUserId}
            setAllotments={setAllotments}
            allotments={allotments}
            type={type!}
          />
          <div>
            <TicketRequestForm
              ticketRequest={ticketRequest}
              setTicketRequest={setTicketRequest}
              allotment={allotment}
              phoneNumberRequired={game?.allowSmsTickets}
            />
            {!isBusinessType && (
              <div style={{ paddingBottom: "5rem" }}>
                <TicketAgreement setAgreed={setAgreed} />
              </div>
            )}
          </div>
        </div>
      </ScrollContainer>
    </TicketRequestProvider>
  );
};

export default TicketRequestComp;
